import { useCallback, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, CalendarCheck, Wallet, HardHat, Plane,
  Banknote, Truck, FileBarChart, FolderOpen, Settings as SettingsIcon,
  Menu as MenuIcon, Sun, Moon, Monitor, LogOut, ChevronDown, AlertTriangle,
  PanelLeftClose, PanelLeftOpen, Search, Table2,
} from 'lucide-react';
import { useSession } from '@/core/session';
import { useTheme, type ColorScheme } from '@/core/theme';
import { useI18n, useT, type TranslationKey } from '@/core/i18n';
import { bilingualName } from '@/core/bilingual';
import { BrandMark } from '@/components/brand';
import { PendingMigrations } from './PendingMigrations';
import { ActingBanner } from './ActingBanner';
import { LanguageToggle } from '@/components/LanguageToggle';
import { useIsDesktop } from '@/core/useMediaQuery';
import { useDismiss } from '@/core/useDismiss';
import { handlePushTaps } from '@/core/push';
import { cn, Pill, Menu, MenuItem } from '@/components/ui';
import { Avatar } from '@/components/Avatar';
import { NotificationBell } from '@/modules/notifications/NotificationBell';
import { QuickNav } from './QuickNav';
import type { NavItem } from './useQuickNav';
import { CommandPalette, useCommandPalette } from '@/components/CommandPalette';
import { useBackToClose } from './useAndroidBack';

/* -----------------------------------------------------------------------------
 * Navigation
 *
 * Each entry declares the grant it needs. An item the signed-in user cannot use
 * is not rendered at all — but that is a tidiness measure, not the security
 * boundary. The database refuses the query regardless.
 * -------------------------------------------------------------------------- */
/* Exported so the quick-bar picker offers exactly what the sidebar does — one
   list, so a module can never appear in one and not the other. */
export const ADMIN_NAV: NavItem[] = [
  { to: '/',            labelKey: 'nav.dashboard',        icon: LayoutDashboard, permission: null },
  { to: '/employees',   labelKey: 'nav.employees',        icon: Users,           permission: 'employees.view' },
  { to: '/attendance',  labelKey: 'nav.attendance',       icon: CalendarCheck,   permission: 'attendance.view' },
  { to: '/payroll',     labelKey: 'nav.payroll',          icon: Wallet,          permission: 'payroll.view' },
  { to: '/salary-summary', labelKey: 'nav.salarySummary', icon: Table2,          permission: 'payroll.view' },
  { to: '/projects',    labelKey: 'nav.projects',         icon: HardHat,         permission: 'projects.view' },
  { to: '/leaves',      labelKey: 'nav.leaves',           icon: Plane,           permission: 'leave.view' },
  { to: '/advances',    labelKey: 'nav.advances', icon: Banknote,        permission: 'advances.view' },
  { to: '/fleet',       labelKey: 'nav.fleet',            icon: Truck,           permission: 'fleet.view' },
  { to: '/reports',     labelKey: 'nav.reports',          icon: FileBarChart,    permission: 'reports.export' },
  { to: '/documents',   labelKey: 'nav.documents',        icon: FolderOpen,      permission: 'employees.view' },
  { to: '/settings',    labelKey: 'nav.settings',         icon: SettingsIcon,    permission: 'settings.manage' },
] as const;

const COLLAPSE_KEY = 'noorone.sidebar';

export function AppShell() {
  const language = useI18n().language;
  const t = useT();
  const { profile, company, can, signOut, policyGaps } = useSession();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useIsDesktop();
  const palette = useCommandPalette();

  const visible = ADMIN_NAV.filter((item) => !item.permission || can(item.permission));

  /**
   * Icons-only is a *desktop* state. On a phone the sidebar is a drawer, and a
   * drawer that also changed width would slide in and widen at the same time —
   * which is what made it feel like it appeared rather than opened.
   */
  const mini = collapsed && isDesktop;

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [collapsed]);

  // A drawer over a scrolling page is disorienting: the content behind it moves
  // while the menu stays put.
  useEffect(() => {
    if (!drawerOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [drawerOpen]);

  // Android back closes the drawer rather than leaving the page behind it.
  useBackToClose(drawerOpen, useCallback(() => setDrawerOpen(false), []));

  /* Tapping a notification opens the thing it is about.
     Without this the app opens on the dashboard and the man has to go and find
     what he was just told, which is most of the reason people stop reading
     notifications at all. Mounted here because the shell outlives every route,
     so the listener is attached once rather than per page. */
  useEffect(() => { void handlePushTaps((url) => navigate(url)); }, [navigate]);

  // Escape closes it, as it should for anything modal.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  return (
    <div className="min-h-dvh bg-bg flex">
      {/* Always mounted rather than conditionally rendered, so it can fade out
          as well as in. Unmounting it made the backdrop vanish instantly while
          the drawer was still sliding away. */}
      <button
        type="button"
        aria-label={t('shell.closeMenu')}
        tabIndex={drawerOpen ? 0 : -1}
        aria-hidden={!drawerOpen}
        onClick={() => setDrawerOpen(false)}
        className={cn(
          'fixed inset-0 z-30 bg-black/50 lg:hidden',
          'transition-opacity duration-300 ease-out',
          drawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
      />

      {/* ---- Sidebar --------------------------------------------------------
          Three parts: a pinned header, one scrolling middle, a pinned footer.

          Two behaviours in one element. On a phone it is a drawer: only
          `transform` animates, on the curve a sheet uses, with a shadow so it
          reads as floating above the page. On a desktop it is a rail: only
          `width` animates, and it never slides at all.
          ------------------------------------------------------------------ */}
      <aside
        style={{ width: mini ? 'var(--sidebar-w-collapsed)' : 'var(--sidebar-w)' }}
        className={cn(
          'fixed lg:sticky top-0 z-40 h-dvh shrink-0',
          'bg-sidebar border-e border-sidebar-line',
          'flex flex-col overflow-hidden',
          // Phone: slide only. Desktop: width only.
          'transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]',
          'lg:transition-[width] lg:duration-200 lg:ease-out',
          'will-change-transform',
          /* The slide has to know which edge it lives on, and must not reach
             the desktop layout at all.
             ---------------------------------------------------------------
             `border-e` puts the sidebar on the reading edge, so on an Arabic
             page it sits at the right — and a drawer that slides off to the
             left then travels the whole width of the screen to hide. There is
             no logical equivalent of translate-x, so this is one of the few
             places that has to name a direction.

             Scoped to max-lg, which is the part that was wrong. `rtl:` compiles
             to an attribute selector and `lg:` to a media query; with equal
             specificity the later rule wins, and rtl: was landing after lg:.
             The desktop sidebar was being pushed a full width off the right of
             the screen — present, laid out, and invisible. Confining the
             transform to the breakpoint that actually has a drawer means the
             two rules can never argue. */
          drawerOpen
            ? 'translate-x-0 shadow-[0_0_40px_-4px_rgba(0,0,0,0.45)] lg:shadow-none'
            : 'max-lg:-translate-x-full max-lg:rtl:translate-x-full',
        )}
      >
        <div
          className={cn(
            'shrink-0 flex items-center gap-2.5 pt-safe',
            'h-[calc(var(--header-h)+var(--safe-top))]',
            'border-b border-sidebar-line',
            mini ? 'justify-center px-2' : 'px-4',
          )}
        >
          {/* On a chip: the sidebar is dark green and so is the mark. */}
          <BrandMark size={28} chip />
          {!mini && (
            <div className="leading-tight min-w-0">
              {/* The company's own Arabic name, which the session has been
                  loading all along and this line was not asking for. A company
                  that has recorded one is called that; one that has not keeps
                  the name it is registered under, which is honest rather than
                  a transliteration nobody chose. */}
              <p className="text-[13.5px] font-bold tracking-tight truncate">
                {company
                  ? bilingualName(company.name, company.nameAr, language)
                  : t('shell.productName')}
              </p>
              {/* Only a second line when it says something the first does not —
                  a company called "NoorOne ERP" should not be printed twice. */}
              <p className="text-[10.5px] text-subtle truncate">
                {company?.name && company.name !== 'NoorOne ERP'
                  ? t('shell.productName')
                  : t('shell.tagline')}
              </p>
            </div>
          )}
        </div>

        {/* One scroll region, holding the nav and everything under it. */}
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden sidebar-scroll">
          <nav className="p-2 flex flex-col gap-0.5">
            {visible.map(({ to, labelKey, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={to === '/'}
                /* The browser cross-fades the old screen into the new one
                   rather than the new one appearing over nothing. See app.css;
                   where it is unsupported this attribute is ignored and the
                   page-enter animation carries on doing the job. */
                viewTransition
                {...(mini ? { title: t(labelKey) } : {})}
                onClick={() => setDrawerOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'relative flex items-center gap-3 h-10 rounded-card shrink-0',
                    /* 13px, not the 13.5 of --text-body. A sidebar is read at a
                       glance and never word by word; it sits a step below the
                       page content on purpose, and putting it on the body size
                       made every label grow. */
                    'text-[13px] font-medium press',
                    mini ? 'justify-center px-0' : 'px-3',
                    /* A rule down the leading edge as well as the tint.
                       -----------------------------------------------------
                       The filled pill alone was doing two jobs badly: on the
                       paler presets it barely registers, and when the sidebar
                       is collapsed to icons there is no shape for it to fill.
                       A 2px marker on the inline-start edge reads instantly at
                       both widths and in all nine presets, because it is the
                       brand colour on a surface rather than a tint of it.

                       `start` rather than `left`, so it stays on the edge
                       nearest the content when the whole app mirrors. */
                    isActive
                      ? 'bg-sidebar-active text-sidebar-active-fg font-semibold'
                        + ' before:absolute before:inset-y-1.5 before:start-0'
                        + ' before:w-[2.5px] before:rounded-full before:bg-brand'
                      : 'text-sidebar-fg hover:bg-surface-2 hover:text-text',
                  )
                }
              >
                <Icon size={17} strokeWidth={2} className="shrink-0" />
                {!mini && <span className="truncate">{t(labelKey)}</span>}
              </NavLink>
            ))}
          </nav>

          {/* Rules incomplete — the single most important thing an admin can be
              told, because nothing calculates until it is resolved. */}
          {policyGaps.length > 0 && can('settings.manage') && (
            <button
              type="button"
              onClick={() => { navigate('/settings/rules'); setDrawerOpen(false); }}
              {...(mini
                ? { title: t('shell.settingsUnset', { count: policyGaps.length }) }
                : {})}
              className={cn(
                'mx-2 mb-2 rounded-card bg-warn-subtle text-start',
                mini ? 'p-2 flex justify-center' : 'p-3',
              )}
            >
              {mini ? (
                <span className="relative text-warn">
                  <AlertTriangle size={17} />
                  <span className="absolute -top-1.5 -end-2 min-w-[15px] h-[15px] px-1 rounded-pill bg-warn text-warn-fg text-[9px] font-bold grid place-items-center">
                    {policyGaps.length}
                  </span>
                </span>
              ) : (
                <>
                  <span className="flex items-center gap-2 text-warn font-semibold text-[12px]">
                    <AlertTriangle size={14} />
                    {t('shell.rulesIncomplete')}
                  </span>
                  <span className="block text-[11.5px] text-muted mt-1 leading-snug">
                    {policyGaps.length} setting{policyGaps.length === 1 ? '' : 's'} still
                    unset. Payroll cannot run until they are.
                  </span>
                </>
              )}
            </button>
          )}
        </div>

        <div className="shrink-0 border-t border-sidebar-line p-2">
          <div className={cn('flex items-center gap-2.5 py-1', mini ? 'justify-center' : 'px-1')}>
            {/* Their own face, or their initials — one component, because
                the second case is the ordinary one and had two spellings of it
                in this file already. */}
            <Avatar
              name={profile
                ? bilingualName(profile.fullName, profile.fullNameAr, language)
                : null}
              path={profile?.avatarPath}
              size="sm"
            />
            {!mini && (
              <>
                <div className="min-w-0 flex-1">
                  {/* His own name as he writes it, and the role in the
                      language he is reading. A role is a word this company
                      chose — Payroll Officer, مسؤول الرواتب — and sat in
                      English under an Arabic sidebar because nothing had asked
                      the database for the other half. */}
                  <p className="text-[12.5px] font-semibold truncate">
                    {profile
                      ? bilingualName(profile.fullName, profile.fullNameAr, language)
                      : '—'}
                  </p>
                  <p className="text-[10.5px] text-subtle truncate">
                    {profile
                      ? bilingualName(profile.roleName, profile.roleNameAr, language)
                      : '—'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => void signOut()}
                  aria-label={t('shell.signOut')}
                  className="p-1.5 rounded-card text-subtle hover:text-danger hover:bg-danger-subtle transition-colors"
                >
                  <LogOut size={15} />
                </button>
              </>
            )}
          </div>
        </div>
      </aside>

      {/* ---- Main --------------------------------------------------------- */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* The padding reserves the status bar's height; the background fills
            it, so the clock sits on the header's own colour rather than on
            whatever the window happens to be painted. */}
        {/* The rule under the header is the only thing separating it from the
            page, which is the point — a shadow here would be the one piece of
            elevation on a screen that has otherwise given it up. */}
        <header className="sticky top-0 z-20 bg-surface lg:bg-surface/85 lg:backdrop-blur border-b border-line pt-safe">
          <div className="h-[var(--header-h)] flex items-center gap-1 px-3 lg:px-5">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label={t('shell.openMenu')}
            className="lg:hidden p-2 rounded-card text-muted hover:bg-surface-2"
          >
            <MenuIcon size={19} />
          </button>

          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            aria-label={collapsed ? t('shell.expandSidebar') : t('shell.collapseSidebar')}
            title={collapsed ? t('shell.expandSidebar') : t('shell.collapseSidebar')}
            className="hidden lg:grid place-items-center p-2 rounded-card text-muted hover:bg-surface-2 hover:text-text transition-colors"
          >
            {/* Mirrored in Arabic. These icons draw a panel on the left with
                an arrow pointing away from it, which is the correct picture
                only while the sidebar is on the left. In Arabic it is on the
                right, and the unflipped icon points at the sidebar it would
                open and away from the one it would close — telling somebody
                the opposite of what the button does. */}
            {collapsed
              ? <PanelLeftOpen size={18} className="flip-rtl" />
              : <PanelLeftClose size={18} className="flip-rtl" />}
          </button>

          {/* Wide enough to read as a search field, not a hidden shortcut —
              nobody discovers Ctrl+K on their own. */}
          <button
            type="button"
            onClick={() => palette.setOpen(true)}
            className={cn(
              'hidden sm:flex items-center gap-2 h-9 ps-3 pe-2 ms-2 rounded-card',
              'border border-line bg-surface-2/60 text-subtle',
              'hover:border-line-strong hover:text-muted transition-colors',
              'w-[220px] lg:w-[280px]',
            )}
          >
            <Search size={15} className="shrink-0" />
            <span className="text-[12.5px] flex-1 text-start">Search…</span>
            <kbd className="hidden lg:inline text-[10px] px-1.5 py-0.5 rounded border border-line bg-surface">
              Ctrl K
            </kbd>
          </button>

          <button
            type="button"
            onClick={() => palette.setOpen(true)}
            aria-label={t('shell.search')}
            className="sm:hidden p-2 rounded-card text-muted hover:bg-surface-2 hover:text-text transition-colors"
          >
            <Search size={18} />
          </button>

          <div className="flex-1" />

          <LanguageToggle />
          <SchemeToggle />
          <NotificationBell />
          </div>
        </header>

        {/* Under the header and above everything else, sticky, on every page.
            The one thing on this screen that says whose company it is. */}
        <ActingBanner />

        {/* Room for the quick bar, which is fixed and would otherwise sit on
            top of whatever is at the bottom of the page. Only on the screens
            that have one. */}
        <main
          key={location.pathname}
          className="flex-1 p-4 lg:p-6 page-enter pb-24 lg:pb-6"
        >
          {/* Above whatever the page is, because the thing it explains is that
              the page has nothing on it. */}
          <PendingMigrations />
          <Outlet />
        </main>
      </div>

      <QuickNav items={visible} />

      <CommandPalette open={palette.open} onClose={() => palette.setOpen(false)} />
    </div>
  );
}

/* -----------------------------------------------------------------------------
 * Light / dark toggle — personal, and available to everyone.
 * The company *preset* is admin-only and lives in Settings → Appearance.
 * -------------------------------------------------------------------------- */
export function SchemeToggle() {
  const t = useT();
  const { scheme } = useTheme();
  // Goes through the session, not the theme provider, so the choice is written
  // to the account rather than being reset by it on the next load.
  const { setColorScheme } = useSession();
  const [open, setOpen] = useState(false);

  const options: Array<{
    value: ColorScheme; labelKey: TranslationKey; icon: typeof Sun;
  }> = [
    { value: 'light',  labelKey: 'nav.light' as const,       icon: Sun },
    { value: 'dark',   labelKey: 'nav.dark' as const,        icon: Moon },
    { value: 'system', labelKey: 'nav.matchDevice' as const, icon: Monitor },
  ];

  const active = options.find((o) => o.value === scheme) ?? options[2]!;
  const ActiveIcon = active.icon;

  // Wraps the trigger as well as the menu — see useDismiss for why.
  const ref = useDismiss<HTMLDivElement>(open, () => setOpen(false));

  return (
    <div className="relative" ref={ref}>
      {/* data-scheme-toggle is a stable handle for the settings screen, which
          offers a button to open this menu for anybody who has not spotted it
          in the header. That button used to find this element by its
          aria-label — English, and no longer matching once the label was
          translated. A label is for a person; a selector needs something that
          does not change with who is reading. */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t('set.appearance')}
        data-scheme-toggle=""
        aria-expanded={open}
        className="flex items-center gap-1 p-2 rounded-card text-muted hover:bg-surface-2 hover:text-text transition-colors"
      >
        <ActiveIcon size={18} />
        <ChevronDown size={13} />
      </button>

      {open && (
        <Menu className="w-44">
          {options.map(({ value, labelKey, icon: Icon }) => (
            <MenuItem
              key={value}
              active={scheme === value}
              icon={<Icon size={15} className="shrink-0" />}
              onClick={() => { setColorScheme(value); setOpen(false); }}
            >
              {t(labelKey)}
            </MenuItem>
          ))}
        </Menu>
      )}
    </div>
  );
}

export function PermissionDenied() {
  const t = useT();
  return (
    <div className="max-w-md mx-auto text-center py-20">
      <Pill tone="danger">{t('shell.notPermitted')}</Pill>
      <h1 className="text-xl font-bold mt-4">{t('shell.notOpenToRole')}</h1>
      <p className="text-[13px] text-muted mt-2">
        {t('shell.askAdmin')}
      </p>
    </div>
  );
}

